import React, { useState, useEffect, useCallback } from 'react';
import api from '../api';
import LiveBoard from './Board';
import { formatCurrency } from '../utils/currency';

// ─── CSV export ───────────────────────────────────────────────────────────────
function exportCSV(rows, cols, filename) {
  const header = cols.map(c => c.label).join(',');
  const body   = rows.map(row =>
    cols.map(c => {
      const v = row[c.key] ?? '';
      return String(v).includes(',') || String(v).includes('"') ? `"${String(v).replace(/"/g,'""')}"` : v;
    }).join(',')
  );
  const blob = new Blob([[header, ...body].join('\n')], { type: 'text/csv' });
  const url  = URL.createObjectURL(blob);
  const a    = document.createElement('a'); a.href = url; a.download = filename; a.click();
  URL.revokeObjectURL(url);
}

// ─── Shared filter bar styles ─────────────────────────────────────────────────
const inp = { background: 'var(--bg-primary)', border: '1px solid var(--border)', borderRadius: 4, color: 'var(--text-primary)', padding: '7px 11px', fontFamily: 'var(--font-body)', fontSize: 13 };

function DateRange({ from, to, onFrom, onTo }) {
  return (
    <>
      <input type="date" value={from} onChange={e => onFrom(e.target.value)} style={inp} />
      <span style={{ color: 'var(--text-muted)', fontFamily: 'var(--font-mono)', fontSize: 11 }}>to</span>
      <input type="date" value={to}   onChange={e => onTo(e.target.value)}   style={inp} />
    </>
  );
}

function defaultRange() {
  const to   = new Date().toISOString().slice(0,10);
  const from = new Date(Date.now() - 30*86400000).toISOString().slice(0,10);
  return { from, to };
}

// ─── Reusable report table ────────────────────────────────────────────────────
function ReportTable({ rows, cols, loading, emptyMsg = 'No data for the selected filters' }) {
  if (loading) return <div className="loading">Loading…</div>;
  if (!rows.length) return <div className="empty-state">{emptyMsg}</div>;
  return (
    <div className="table-wrap">
      <table>
        <thead>
          <tr>{cols.map(c => <th key={c.key}>{c.label}</th>)}</tr>
        </thead>
        <tbody>
          {rows.map((row, i) => (
            <tr key={i}>
              {cols.map(c => (
                <td key={c.key} style={c.style?.(row) || {}}>
                  {c.fmt ? c.fmt(row[c.key], row) : (row[c.key] ?? '—')}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ─── Report header row ────────────────────────────────────────────────────────
function ReportHeader({ title, rows, cols, filename, children }) {
  return (
    <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12, marginBottom: 20 }}>
      <div>
        <div style={{ fontFamily: 'var(--font-display)', fontSize: 15, fontWeight: 700, letterSpacing: 1, textTransform: 'uppercase', color: 'var(--text-secondary)', marginBottom: 8 }}>{title}</div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>{children}</div>
      </div>
      <button
        className="btn btn-secondary btn-sm"
        onClick={() => exportCSV(rows, cols, filename)}
        disabled={!rows.length}
      >
        ↓ Export CSV
      </button>
    </div>
  );
}

// ─── 1. Production Hours ──────────────────────────────────────────────────────
function ProductionHours({ projects, workCentres, staff }) {
  const dr = defaultRange();
  const [from, setFrom] = useState(dr.from);
  const [to,   setTo]   = useState(dr.to);
  const [projFilter, setProjFilter] = useState('');
  const [wcFilter,   setWcFilter]   = useState('');
  const [sfFilter,   setSfFilter]   = useState('');
  const [rows,    setRows]    = useState([]);
  const [loading, setLoading] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    const p = new URLSearchParams({ date_from: from, date_to: to });
    if (projFilter) p.set('project_id',     projFilter);
    if (wcFilter)   p.set('work_centre_id', wcFilter);
    if (sfFilter)   p.set('staff_id',       sfFilter);
    api.get(`/reports/production-hours?${p}`)
      .then(r => { setRows(r.data.value || []); setLoading(false); })
      .catch(() => setLoading(false));
  }, [from, to, projFilter, wcFilter, sfFilter]);

  useEffect(() => { load(); }, [load]);

  const cols = [
    { key: 'work_centre',   label: 'Work Centre' },
    { key: 'staff',         label: 'Staff' },
    { key: 'job_number',    label: 'Job #', style: () => ({ fontFamily: 'var(--font-mono)', fontSize: 12, color: 'var(--accent)' }) },
    { key: 'project_title', label: 'Project' },
    { key: 'total_hours',   label: 'Hours', fmt: v => <strong>{v}</strong> },
    { key: 'entries',       label: 'Entries' },
    { key: 'first_date',    label: 'First', style: () => ({ color: 'var(--text-muted)', fontSize: 12 }) },
    { key: 'last_date',     label: 'Last',  style: () => ({ color: 'var(--text-muted)', fontSize: 12 }) },
  ];

  const total = rows.reduce((s, r) => s + parseFloat(r.total_hours || 0), 0);

  return (
    <div>
      <ReportHeader title="Production Hours" rows={rows} cols={cols} filename={`production-hours-${from}-${to}.csv`}>
        <DateRange from={from} to={to} onFrom={setFrom} onTo={setTo} />
        <select value={projFilter} onChange={e => setProjFilter(e.target.value)} style={inp}>
          <option value="">All Projects</option>
          {projects.map(p => <option key={p.id} value={p.id}>{p.job_number} — {p.title}</option>)}
        </select>
        <select value={wcFilter} onChange={e => setWcFilter(e.target.value)} style={inp}>
          <option value="">All Work Centres</option>
          {workCentres.map(w => <option key={w.id} value={w.id}>{w.name}</option>)}
        </select>
        <select value={sfFilter} onChange={e => setSfFilter(e.target.value)} style={inp}>
          <option value="">All Staff</option>
          {staff.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
        </select>
      </ReportHeader>
      {total > 0 && (
        <div style={{ fontFamily: 'var(--font-mono)', fontSize: 11, color: 'var(--text-muted)', marginBottom: 12 }}>
          {rows.length} rows · <strong style={{ color: 'var(--accent)' }}>{total.toFixed(2)}h</strong> total
        </div>
      )}
      <div className="card"><ReportTable rows={rows} cols={cols} loading={loading} /></div>
    </div>
  );
}

// ─── 2. Job Cost ──────────────────────────────────────────────────────────────
function JobCost({ projects }) {
  const [projFilter, setProjFilter] = useState('');
  const [rows,    setRows]    = useState([]);
  const [rate,    setRate]    = useState(0);
  const [loading, setLoading] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    const p = new URLSearchParams();
    if (projFilter) p.set('project_id', projFilter);
    api.get(`/reports/job-cost?${p}`)
      .then(r => { setRows(r.data.value || []); setRate(r.data.hourly_rate || 0); setLoading(false); })
      .catch(() => setLoading(false));
  }, [projFilter]);

  useEffect(() => { load(); }, [load]);

  const cols = [
    { key: 'job_number',     label: 'Job #', style: () => ({ fontFamily: 'var(--font-mono)', fontSize: 12, color: 'var(--accent)' }) },
    { key: 'project_title',  label: 'Project' },
    { key: 'status',         label: 'Status', fmt: v => <span className={`badge badge-${v}`}>{v?.replace(/_/g,' ')}</span> },
    { key: 'work_centre',    label: 'Work Centre' },
    { key: 'total_hours',    label: 'Hours', fmt: v => <strong>{v}</strong> },
    { key: 'labour_cost',    label: 'Labour Cost ($)', fmt: v => formatCurrency(parseFloat(v || 0)), style: () => ({ color: 'var(--success)', fontFamily: 'var(--font-mono)' }) },
    { key: 'quoted_value',   label: 'Quoted ($)', fmt: v => v != null ? formatCurrency(parseFloat(v)) : '—', style: () => ({ fontFamily: 'var(--font-mono)', color: 'var(--text-muted)' }) },
];

  return (
    <div>
      <ReportHeader title="Job Cost" rows={rows} cols={cols} filename="job-cost.csv">
        <select value={projFilter} onChange={e => setProjFilter(e.target.value)} style={inp}>
          <option value="">All Projects</option>
          {projects.map(p => <option key={p.id} value={p.id}>{p.job_number} — {p.title}</option>)}
        </select>
        {rate > 0 && <span style={{ fontFamily: 'var(--font-mono)', fontSize: 11, color: 'var(--text-muted)' }}>@ {formatCurrency(parseFloat(rate))}/hr</span>}
      </ReportHeader>
      <div className="card"><ReportTable rows={rows} cols={cols} loading={loading} /></div>
    </div>
  );
}

// ─── 3. Non-Productive Time ───────────────────────────────────────────────────
function NonProductive() {
  const dr = defaultRange();
  const [from, setFrom] = useState(dr.from);
  const [to,   setTo]   = useState(dr.to);
  const [rows,    setRows]    = useState([]);
  const [loading, setLoading] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    api.get(`/reports/non-productive?date_from=${from}&date_to=${to}`)
      .then(r => { setRows(r.data.value || []); setLoading(false); })
      .catch(() => setLoading(false));
  }, [from, to]);

  useEffect(() => { load(); }, [load]);

  const cols = [
    { key: 'log_date',    label: 'Date',     style: () => ({ fontFamily: 'var(--font-mono)', fontSize: 12, color: 'var(--text-muted)' }) },
    { key: 'staff',       label: 'Staff' },
    { key: 'activity',    label: 'Activity', style: () => ({ color: 'var(--info)' }) },
    { key: 'total_hours', label: 'Hours',    fmt: v => <strong>{v}</strong> },
    { key: 'entries',     label: 'Entries' },
  ];
  const total = rows.reduce((s, r) => s + parseFloat(r.total_hours || 0), 0);

  return (
    <div>
      <ReportHeader title="Non-Productive Time" rows={rows} cols={cols} filename={`non-productive-${from}-${to}.csv`}>
        <DateRange from={from} to={to} onFrom={setFrom} onTo={setTo} />
      </ReportHeader>
      {total > 0 && (
        <div style={{ fontFamily: 'var(--font-mono)', fontSize: 11, color: 'var(--text-muted)', marginBottom: 12 }}>
          {rows.length} rows · <strong style={{ color: 'var(--info)' }}>{total.toFixed(2)}h</strong> non-productive
        </div>
      )}
      <div className="card"><ReportTable rows={rows} cols={cols} loading={loading} /></div>
    </div>
  );
}

// ─── 4. Capacity ─────────────────────────────────────────────────────────────
function Capacity() {
  const dr = defaultRange();
  const [from, setFrom] = useState(dr.from);
  const [to,   setTo]   = useState(dr.to);
  const [rows,    setRows]    = useState([]);
  const [loading, setLoading] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    api.get(`/reports/capacity?date_from=${from}&date_to=${to}`)
      .then(r => { setRows(r.data.value || []); setLoading(false); })
      .catch(() => setLoading(false));
  }, [from, to]);

  useEffect(() => { load(); }, [load]);

  const cols = [
    { key: 'log_date',    label: 'Date',        style: () => ({ fontFamily: 'var(--font-mono)', fontSize: 12 }) },
    { key: 'day_name',    label: 'Day',         style: () => ({ color: 'var(--text-muted)', fontSize: 12 }) },
    { key: 'work_centre', label: 'Work Centre' },
    { key: 'total_hours', label: 'Hours',       fmt: v => <strong>{v}</strong> },
    { key: 'staff_count', label: 'Staff',       style: () => ({ fontFamily: 'var(--font-mono)', fontSize: 12 }) },
  ];

  return (
    <div>
      <ReportHeader title="Capacity" rows={rows} cols={cols} filename={`capacity-${from}-${to}.csv`}>
        <DateRange from={from} to={to} onFrom={setFrom} onTo={setTo} />
      </ReportHeader>
      <div className="card"><ReportTable rows={rows} cols={cols} loading={loading} /></div>
    </div>
  );
}

// ─── Reports main page ────────────────────────────────────────────────────────
function ReportsInner() {
  const [tab, setTab] = useState('board');
  const [projects,    setProjects]    = useState([]);
  const [workCentres, setWorkCentres] = useState([]);
  const [allStaff,    setAllStaff]    = useState([]);

  useEffect(() => {
    api.get('/projects').then(r => setProjects(r.data.value || []));
    api.get('/work-centres').then(r => setWorkCentres(r.data.value || []));
    api.get('/staff/all').then(r => setAllStaff(r.data.value || []));
  }, []);

  const tabs = [
    { id: 'board',        label: '◉ Live Board' },
    { id: 'hours',        label: '⏱ Prod. Hours' },
    { id: 'jobcost',      label: '$ Job Cost' },
    { id: 'nonprod',      label: '↻ Non-Productive' },
    { id: 'capacity',     label: '▦ Capacity' },
  ];

  return (
    <div>
      <div className="page-header">
        <h2 className="page-title">RE<span>PORTS</span></h2>
      </div>

      {/* Tab nav */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 28, flexWrap: 'wrap' }}>
        {tabs.map(t => (
          <button key={t.id} onClick={() => setTab(t.id)} style={{
            padding: '9px 16px', borderRadius: 4, cursor: 'pointer', transition: 'all 0.15s', whiteSpace: 'nowrap',
            border:     tab === t.id ? '1px solid var(--accent)' : '1px solid var(--border)',
            background: tab === t.id ? 'var(--accent-glow)'      : 'var(--bg-card)',
            color:      tab === t.id ? 'var(--accent)'            : 'var(--text-secondary)',
            fontFamily: 'var(--font-display)', fontSize: 13, fontWeight: 600,
            letterSpacing: 1, textTransform: 'uppercase',
          }}>
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'board'    && <div className="card"><LiveBoard showForceOff={true} /></div>}
      {tab === 'hours'    && <ProductionHours projects={projects} workCentres={workCentres} staff={allStaff} />}
      {tab === 'jobcost'  && <JobCost projects={projects} />}
      {tab === 'nonprod'  && <NonProductive />}
      {tab === 'capacity' && <Capacity />}
    </div>
  );
}

export default function Reports() {
  return <ReportsInner />;
}
